/**
 * Helper functions for stories & chapters
 */

import { COVER_TONES, WORDS_PER_MINUTE } from '../constants';

/**
 * Strip HTML tags and count words in a chapter's content
 */
export function countWords(html) {
  if (!html) return 0;
  const text = html.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').trim();
  if (!text) return 0;
  return text.split(/\s+/).filter(Boolean).length;
}

/**
 * Total word count across all chapters of a story
 */
export function getStoryWordCount(story) {
  return (story.chapters ?? []).reduce((sum, ch) => sum + countWords(ch.content), 0);
}

/**
 * Estimated reading time in minutes
 */
export function getReadingTime(wordCount) {
  return Math.max(1, Math.ceil(wordCount / WORDS_PER_MINUTE));
}

/**
 * Deterministic cover tone for a story, based on its id
 */
export function getCoverTone(storyId) {
  const str = String(storyId);
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 31 + str.charCodeAt(i)) >>> 0;
  }
  return COVER_TONES[hash % COVER_TONES.length];
}

/**
 * First letter(s) to show on a typographic cover
 */
export function getCoverInitial(title) {
  const trimmed = (title ?? '').trim();
  if (!trimmed) return '?';
  return trimmed[0].toUpperCase();
}

/**
 * Format a timestamp as relative time, e.g. "18 minutes ago"
 */
export function formatRelativeTime(timestamp) {
  if (!timestamp) return '';
  const diffMs = Date.now() - timestamp;
  const diffSec = Math.round(diffMs / 1000);
  if (diffSec < 10) return 'just now';
  if (diffSec < 60) return `${diffSec} seconds ago`;
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `${diffMin} minute${diffMin === 1 ? '' : 's'} ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr} hour${diffHr === 1 ? '' : 's'} ago`;
  const diffDay = Math.round(diffHr / 24);
  if (diffDay === 1) return 'yesterday';
  if (diffDay < 7) return `${diffDay} days ago`;
  return new Date(timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * Format a timestamp as an absolute date, e.g. "Aug 20, 2026"
 */
export function formatDate(timestamp) {
  if (!timestamp) return '';
  return new Date(timestamp).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * The chapter a writer was most recently editing
 */
export function getLastEditedChapter(story) {
  const chapters = story.chapters ?? [];
  if (chapters.length === 0) return null;
  return [...chapters].sort((a, b) => b.updatedAt - a.updatedAt)[0];
}

/**
 * Sort helpers for the dashboard grid
 */
export function sortStories(stories, sortBy) {
  const sorted = [...stories];
  switch (sortBy) {
    case 'title':
      return sorted.sort((a, b) => a.title.localeCompare(b.title));
    case 'createdAt':
      return sorted.sort((a, b) => b.createdAt - a.createdAt);
    case 'wordCount':
      return sorted.sort((a, b) => getStoryWordCount(b) - getStoryWordCount(a));
    case 'updatedAt':
    default:
      return sorted.sort((a, b) => b.updatedAt - a.updatedAt);
  }
}

/**
 * Plain-text extract of a chapter's HTML content, for previews/exports
 */
export function htmlToPlainText(html) {
  if (!html) return '';
  return html
    .replace(/<\/(p|div|h[1-6]|li|blockquote)>/gi, '\n')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/**
 * Build a downloadable export blob for a story
 */
export function buildExport(story, format) {
  const chapters = story.chapters ?? [];
  if (format === 'markdown') {
    const body = chapters
      .map((ch, i) => `## ${i + 1}. ${ch.title}\n\n${htmlToPlainText(ch.content)}`)
      .join('\n\n---\n\n');
    return { content: `# ${story.title}\n\n${story.description ?? ''}\n\n${body}`, ext: 'md', mime: 'text/markdown' };
  }
  const body = chapters
    .map((ch, i) => `${i + 1}. ${ch.title}\n\n${htmlToPlainText(ch.content)}`)
    .join('\n\n---\n\n');
  return { content: `${story.title}\n\n${story.description ?? ''}\n\n${body}`, ext: 'txt', mime: 'text/plain' };
}

export function downloadTextFile(filename, content, mime) {
  const blob = new Blob([content], { type: `${mime};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
